import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import { IParkingPaymentsListZoneData, useApi } from 'components/providers/ApiProvider'

interface Filter {
  zone: number
  plateNumber: string
  paidFrom: string
  paidTill: string
  state: string
}

type RootState = {
  filter: Filter
  listZones: IParkingPaymentsListZoneData[]
  loadList: number
  listLoading: boolean
  searchByPlateNumber: string
  listPerPage: number
  listPage: number
}

type RootFunctions = {
  setFilter(filter: Filter): void
  setLoadList(): void
  setListLoading(status: boolean): void
  setLoadSearchList(): void
  setSearchByPlateNumber(value: string): void
  setLoadSearchByPlateNumberList(): void
  setLoadChangePerPageList(perPage: number): void
  setLoadChangePageList(page: number): void
}

const InitialState: RootState = {
  filter: {
    zone: 0,
    plateNumber: '',
    paidFrom: '',
    paidTill: '',
    state: '',
  },
  listZones: [],
  loadList: 0,
  listLoading: false,
  searchByPlateNumber: null,
  listPerPage: 20,
  listPage: 1,
}

export const ReportsContext = React.createContext<[RootState, (state: any) => void]>([InitialState, () => {
}])

export const ParkingPaymentsProvider: FunctionComponent = ({ children }) => {
  const api = useApi()

  const [state, setState] = useState<RootState>(InitialState)

  useEffect(() => {
    api.getParkingPaymentsListZones()
      .then(data => {
        setState((state: RootState) => ({
          ...state,
          listZones: data.zones,
        }))
      })
      .catch(() => {
      })
  }, [])

  return (
    <ReportsContext.Provider value={[state, setState]}>
      {children}
    </ReportsContext.Provider>
  )
}

export const useParkingPayments = (): RootFunctions & RootState => {
  const [state, setState] = useContext(ReportsContext)

  const setFilter = (filter: Filter) => {
    setState((state: RootState) => ({
      ...state,
      filter,
    }))
  }

  const setLoadList = () => {
    setState((state: RootState) => ({
      ...state,
      loadList: state.loadList + 1,
    }))
  }

  const setListLoading = (status: boolean) => {
    setState((state: RootState) => ({
      ...state,
      listLoading: status,
    }))
  }

  const setLoadSearchList = () => {
    setState((state: RootState) => ({
      ...state,
      loadList: state.loadList + 1,
      listPage: 1,
    }))
  }

  const setSearchByPlateNumber = (value: string) => {
    setState((state: RootState) => ({
      ...state,
      searchByPlateNumber: value,
    }))
  }

  const setLoadSearchByPlateNumberList = () => {
    setState((state: RootState) => ({
      ...state,
      filter: {
        ...state.filter,
        plateNumber: state.searchByPlateNumber,
      },
      searchByPlateNumber: null,
      loadList: state.loadList + 1,
      listPage: 1,
    }))
  }

  const setLoadChangePerPageList = (perPage: number) => {
    setState((state: RootState) => ({
      ...state,
      loadList: state.loadList + 1,
      listPerPage: perPage,
      listPage: 1,
    }))
  }

  const setLoadChangePageList = (page: number) => {
    setState((state: RootState) => ({
      ...state,
      loadList: state.loadList + 1,
      listPage: page,
    }))
  }

  return {
    setFilter,
    setLoadList,
    setListLoading,
    setLoadSearchList,
    setSearchByPlateNumber,
    setLoadSearchByPlateNumberList,
    setLoadChangePerPageList,
    setLoadChangePageList,
    ...state,
  }
}
